import {
  ArrowLeft,
  CalendarClock,
  FileText,
  Image as ImageIcon,
  Megaphone,
  Plus,
  RefreshCw,
  Video,
} from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  BROADCASTS_PATH,
  GAP_DAYS,
  STATUS_LABEL,
  draftOf,
  groupBroadcasts,
  newDraft,
  type Broadcast,
  type BroadcastDraft,
} from "./broadcasts";
import { BroadcastEditor } from "./BroadcastEditor";
import { invokeBroadcast, useBroadcasts } from "./useBroadcasts";

const KIND_ICON = { image: ImageIcon, video: Video, document: FileText };

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Sin fecha";

const Stat = ({ label, value }: { label: string; value?: number }) => (
  <span className="text-xs text-muted-foreground">
    <strong className="text-foreground">{value ?? 0}</strong> {label}
  </span>
);

const BroadcastCard = ({
  broadcast,
  onEdit,
  onCancel,
}: {
  broadcast: Broadcast;
  onEdit: () => void;
  onCancel: () => void;
}) => {
  const status = STATUS_LABEL[broadcast.status];
  const Icon = broadcast.material_kind
    ? KIND_ICON[broadcast.material_kind]
    : null;
  const started = ["sending", "sent"].includes(broadcast.status);
  return (
    <div className="flex flex-col gap-2 rounded-2xl border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">{broadcast.title}</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarClock className="h-3 w-3" />
            {when(broadcast.scheduled_at)} ·{" "}
            {broadcast.segment === "clients" ? "Clientes" : "Leads"}
            {Icon ? (
              <>
                {" · "}
                <Icon className="h-3 w-3" />
                {broadcast.material_title}
              </>
            ) : (
              " · Solo texto"
            )}
          </span>
        </div>
        <Badge className={`border-transparent ${status.className}`}>
          {status.label}
        </Badge>
      </div>
      <p className="line-clamp-2 whitespace-pre-wrap text-sm text-muted-foreground">
        {broadcast.body}
      </p>
      {broadcast.status === "rejected" && broadcast.template_reason ? (
        <p className="text-xs text-destructive">
          Motivo de Meta: {broadcast.template_reason}. Cambia el texto y vuelve
          a enviarla.
        </p>
      ) : null}
      {started ? (
        <div className="flex flex-wrap gap-3">
          <Stat label="enviados" value={broadcast.sent} />
          <Stat label="entregados" value={broadcast.delivered} />
          <Stat label="leídos" value={broadcast.read} />
          <Stat label="han respondido" value={broadcast.replied} />
          {broadcast.failed ? (
            <Stat label="fallidos" value={broadcast.failed} />
          ) : null}
          {broadcast.status === "sending" ? (
            <Stat
              label="en cola"
              value={
                (broadcast.total ?? 0) -
                (broadcast.sent ?? 0) -
                (broadcast.failed ?? 0)
              }
            />
          ) : null}
        </div>
      ) : null}
      {!["sent", "cancelled"].includes(broadcast.status) ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onEdit}>
            {["draft", "rejected"].includes(broadcast.status)
              ? "Editar"
              : "Cambiar fecha"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * "Difusiones" (admins): scheduled WhatsApp sends to leads or clients to
 * reactivate them for the setters. Calendar by day, drafts and history.
 */
export const BroadcastsPage = () => {
  const notify = useNotify();
  const { broadcasts, isPending, refetch } = useBroadcasts();
  const [editing, setEditing] = useState<{
    draft: BroadcastDraft;
    locked: boolean;
  } | null>(null);
  const groups = groupBroadcasts(broadcasts);

  // From Marketing → Reactivación: «Preparar difusión» for a group
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const group = searchParams.get("grupo");
    if (!group) return;
    const [kind, key] = group.split(":");
    if ((kind === "pain" || kind === "objection") && key) {
      const label = searchParams.get("etiqueta") ?? key;
      const draft = newDraft();
      setEditing({
        draft: {
          ...draft,
          title: `Reactivación · ${label}`,
          filters: {
            ...draft.filters,
            stages: ["reactivation"],
            group: { kind, key, label },
          },
        },
        locked: false,
      });
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("grupo");
        next.delete("etiqueta");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (action: "cancel" | "refresh", id?: Broadcast["id"]) => {
    try {
      await invokeBroadcast(action, id);
      notify(action === "cancel" ? "Cancelada" : "Estado de Meta actualizado", {
        type: "info",
      });
      await refetch();
    } catch (error: unknown) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to="/whatsapp"
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Mensajes
          </Link>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Megaphone className="h-7 w-7" />
            Difusiones
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Envíos programados por WhatsApp para reactivar leads (y algo de vez
            en cuando a los clientes). Cada uno pasa por Meta antes de salir; se
            envían solos entre las 9:00 y las 21:00, y nadie recibe más de uno
            cada {GAP_DAYS} días.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => act("refresh")}>
            <RefreshCw className="h-4 w-4" />
            Ver si Meta ha respondido
          </Button>
          <Button
            onClick={() => setEditing({ draft: newDraft(), locked: false })}
          >
            <Plus className="h-4 w-4" />
            Nueva difusión
          </Button>
        </div>
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : !broadcasts.length ? (
        <p className="rounded-2xl border p-6 text-sm text-muted-foreground">
          Todavía no hay difusiones. Crea la primera: elige a quién, el vídeo o
          PDF de Material, el texto y el día.
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold capitalize text-muted-foreground">
              {group.title}
            </h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {group.items.map((broadcast) => (
                <BroadcastCard
                  key={String(broadcast.id)}
                  broadcast={broadcast}
                  onEdit={() =>
                    setEditing({
                      draft: draftOf(broadcast),
                      locked: !["draft", "rejected"].includes(broadcast.status),
                    })
                  }
                  onCancel={() => act("cancel", broadcast.id)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {editing ? (
        <BroadcastEditor
          draft={editing.draft}
          locked={editing.locked}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refetch();
          }}
        />
      ) : null}
    </div>
  );
};

BroadcastsPage.path = BROADCASTS_PATH;
